import clsx from 'clsx'
import { motion } from 'framer-motion'

import { useTranslation } from '@/shared/hooks/useTranslation'

import { RewardBadge } from './RewardBadge'

export type RewardGainBadgeProps = {
  /** Experiência ganha. Zero ou ausente omite a métrica. */
  points?: number
  /** Moedas ganhas. Zero ou ausente omite a métrica. */
  coins?: number
  /**
   * Quantidade de recompensas resumidas. Quando presente, o badge vira o
   * agregado e ignora `points`/`coins`.
   */
  overflowCount?: number
  /** Degrada a animação para só opacidade. Lido pelo host, não aqui. */
  reducedMotion?: boolean
  className?: string
}

const CAPSULE_CLASS = [
  'pointer-events-none inline-flex items-center gap-2 rounded-full px-3 py-2',
  // Flutuante, não apoiado: pela Depth Strategy do MASTER §2 isso é sombra,
  // nunca contorno.
  'bg-white'
].join(' ')

/**
 * O halo é o que separa a cápsula branca da página (#F9F7FF) e, de quebra,
 * codifica a métrica: roxo é experiência, âmbar é moeda. Quem vê o badge de
 * canto de olho já sabe o que ganhou antes de ler o número.
 */
const haloFor = (hasPoints: boolean, hasCoins: boolean) => {
  if (hasPoints && hasCoins) return 'var(--shadow-reward-both)'
  if (hasCoins) return 'var(--shadow-reward-coins)'
  return 'var(--shadow-reward-points)'
}

// Com movimento reduzido sobra só a opacidade — translate e scale são
// exatamente o que a preferência pede para suprimir.
const FULL_MOTION = {
  initial: { opacity: 0, y: 8, scale: 0.92 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 8, scale: 0.92 },
  transition: { duration: 0.28, ease: [0.34, 1.56, 0.64, 1] as const }
}

const REDUCED_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.15 }
}

/**
 * Cápsula flutuante que anuncia um ganho de experiência ou moedas — o momento de
 * celebração que o participante recebe ao concluir algo.
 *
 * Compõe `RewardBadge` em vez de redesenhar a cápsula: a arte de estrela e moeda
 * é a mesma do resto do produto. Os rótulos vêm sempre do i18n — o `RewardBadge`
 * traz defaults em português, e aceitá-los deixaria as chaves órfãs e o `en`
 * quebrado.
 *
 * Com `overflowCount`, vira o agregado do excedente da fila: usa `stamp`, e não
 * estrela ou moeda, porque o agregado não sabe a composição do que resume e
 * prometer uma métrica específica ali seria informação inventada.
 *
 * O anúncio para leitor de tela é um nó próprio: lida em voz alta, a cápsula
 * `+25` vira "mais vinte e cinco" sem dizer de quê.
 *
 * @example
 * <RewardGainBadge points={150} coins={20} />
 * <RewardGainBadge overflowCount={9} />
 */
export const RewardGainBadge = ({
  points = 0,
  coins = 0,
  overflowCount,
  reducedMotion = false,
  className
}: RewardGainBadgeProps) => {
  const { t } = useTranslation()

  const isOverflow = typeof overflowCount === 'number' && overflowCount > 0
  const hasPoints = points > 0
  const hasCoins = coins > 0

  if (!isOverflow && !hasPoints && !hasCoins) return null

  const summary = isOverflow
    ? t('wallet.rewardGain.overflow', { count: overflowCount })
    : [
        hasPoints ? t('wallet.rewardGain.pointsUnit', { value: points }) : null,
        hasCoins ? t('wallet.rewardGain.coinsUnit', { value: coins }) : null
      ]
        .filter(Boolean)
        // O conectivo também é traduzido: " e " em pt, " and " em en.
        .join(t('wallet.rewardGain.summaryJoin'))

  const anim = reducedMotion ? REDUCED_MOTION : FULL_MOTION
  // O agregado não sabe a composição do que resume, então herda o halo de
  // experiência — a métrica dominante do produto.
  const halo = haloFor(isOverflow || hasPoints, !isOverflow && hasCoins)

  return (
    <motion.div
      className={clsx(CAPSULE_CLASS, className)}
      style={{ boxShadow: halo }}
      {...anim}
    >
      {isOverflow ? (
        <RewardBadge
          type="stamp"
          value={`+${overflowCount}`}
          label={t('wallet.rewardGain.overflowLabel')}
          variant="inline"
        />
      ) : (
        <RewardBadge.Group>
          {hasPoints && (
            <RewardBadge
              type="xp"
              value={`+${points}`}
              label={t('wallet.rewardGain.pointsLabel')}
              variant="inline"
            />
          )}
          {hasCoins && (
            <RewardBadge
              type="coins"
              value={`+${coins}`}
              label={t('wallet.rewardGain.coinsLabel')}
              variant="inline"
            />
          )}
        </RewardBadge.Group>
      )}

      <span className="sr-only">
        {t('wallet.rewardGain.announcement', { summary })}
      </span>
    </motion.div>
  )
}

RewardGainBadge.displayName = 'RewardGainBadge'
